import { Box,Container, Card, Input, InputAdornment, Menu, CircularProgress, Link, Button, Select, Typography, Stack, MenuItem, IconButton } from "@mui/material";
import { useState, useRef, useEffect, useCallback } from 'react';
import { MapTypeSelector, useMapTypeSelector } from "src/pages/app/eld/eld-landmarks/eld-landmark-map-modal";
import L from "leaflet";
import 'leaflet-providers';
import 'leaflet/dist/leaflet.css';
import Logo from 'src/components/logo';
import NoneFound from "src/components/none-found";
import { useParams } from "react-router";
import { Device } from "src/models";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import { useSettingsContext } from 'src/components/settings';

type PageModel = {
  deviceDataResponse: {device?: Device, devices?: Device[]} | {message: string},
}

export default function Page() {
  const settings = useSettingsContext();
  const { shareLinkUuid } = useParams();

  const dataLoader = useCallback(() => ({
    deviceDataResponse: axios.get(formatEndpoint(endpoints.devices.findByShareLinkUuid, {shareLinkUuid})).then(response => response.data),
  }), [shareLinkUuid]);

  const {
    data,
    updateData,
    setData,
    loaded,
    error,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);


  return <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && <>
          {'message' in data.deviceDataResponse
            ? <Box p={4}>
              <NoneFound title={data.deviceDataResponse.message} message="Please contact the owner of the Account">
                <Logo dark />
              </NoneFound>
            </Box>
            : <Box position="absolute" left="0px" top="0px" right="0px" bottom="-15px" display="flex" flexDirection="row">
              <Map devices={data.deviceDataResponse.device ? [data.deviceDataResponse.device] : data.deviceDataResponse.devices!} />
            </Box> }
        </>}
      </DisplayPageData>
    </Container>;
}



function Map({ devices }: { devices: Device[]}) {
  const [node, setNode] = useState(null);

  const mapTypeSelectorState = useMapTypeSelector();

  useEffect(() => {
    const map = (node as any)?.map as L.Map | undefined;
    if (map) {
      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          map.removeLayer(layer);
        }
      });
      const currentZoom = map.getZoom();
      const currentCenter = map.getCenter();

      if (mapTypeSelectorState.defaultSelected) {
        L.tileLayer.provider('Stadia.AlidadeSmooth').addTo(map)
      } else {
        L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg', {
          minZoom: 0,
          maxZoom: 20,
        }).addTo(map);
      }
      map.setView(currentCenter, currentZoom);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(node as any)?.map, mapTypeSelectorState.defaultSelected]);


  useEffect(() => {
    if (node && !(node as any).map) {

      const _map = L.map('map', { attributionControl: false }).setView([40, -96], 5);

      if (mapTypeSelectorState.defaultSelected) {
        L.tileLayer.provider('Stadia.AlidadeSmooth').addTo(_map)
      } else {
        L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg', {
          minZoom: 0,
          maxZoom: 20,
        }).addTo(_map);
      }

      const triangle = L.divIcon({
        html: `
          <svg xmlns="http://www.w3.org/2000/svg" width="14px" height="14px" viewBox="0 0 56 56">
            <rect x="3" y="3" width="50" height="50" rx="6" fill="#36b746" stroke="white" stroke-width="3"/>
          </svg>
        `,
        className: '',
        iconSize: [14, 14],
      });

      devices.forEach((device) => {
        const marker = new L.Marker([Number(device.lastPosLat), Number(device.lastPosLong)], { icon: triangle });

        const popup = L.popup({
          closeButton: false,
          content: `<a href="https://maps.google.com/?q=${device.lastPosLat},${device.lastPosLong}">${device.deviceType}</a>`
        });
        marker.bindPopup(popup);

        marker.addTo(_map);
      });

      (node as any).map = _map;

      return () => {
        if (_map && _map.remove) {
          _map.off();
          _map.remove();
        }
      }
    }
    return () => { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);

  return <Box sx={{ flexGrow: 1, position: "relative" }}>
    <MapTypeSelector mapTypeSelectorState={mapTypeSelectorState} />

    {devices.length === 1 && <Box sx={{
      position: "absolute",
      top: "20px",
      right: "20px",
      zIndex: "1000",
      background: "white",
      padding: "5px",
      borderRadius: "4px",
      boxShadow: "0 0 10px rgba(0,0,0,0.2)",
      display:"flex",
      flexDirection:"row",
      gap: "5px"
    }}>
      {devices[0].deviceType}
    </Box>}

    <div className="parent-container" ref={setNode as any}>
      <div className="child-div" id="map" />
    </div>
  </Box>
}